import { ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import { useBulkDelete, useCustomToast } from '@/shared/lib';
import RenameTitleModal from '../ui/RenameTitleModal.vue';
import { deleteDraft, deleteDrafts, renameDraft } from '../api/matrices.api';
import type { DraftItem } from './models.types';

const DRAFT_TITLE_MAX_LENGTH = 60;
const RENAME_ERROR_MESSAGE = 'Não foi possível renomear o rascunho. Tente novamente.';

export interface DraftActionsOptions {
  patchRenamed: (id: number, title: string, updatedAt: string) => void;
  refresh: () => Promise<void>;
  selectedIds: () => number[];
  removeFromSelection: (ids: number[]) => void;
}

export function useDraftActions(options: DraftActionsOptions) {
  const toast = useCustomToast();

  const renamingId = ref<number | null>(null);
  const renameInitialTitle = ref('');
  const isRenaming = ref(false);

  const renameModal = useModal({
    component: RenameTitleModal,
    attrs: {
      placeholder: 'Digite o título do rascunho',
      maxLength: DRAFT_TITLE_MAX_LENGTH,
      get initialTitle() {
        return renameInitialTitle.value;
      },
      get isSaving() {
        return isRenaming.value;
      },
      onClose: () => closeRename(),
      onSave: (title: string) => handleRename(title),
    },
  });

  const closeRename = () => {
    renameModal.close();
    renamingId.value = null;
  };

  const openRename = (draft: DraftItem) => {
    renamingId.value = draft.id;
    renameInitialTitle.value = draft.title;
    return renameModal.open();
  };

  const handleRename = async (title: string) => {
    const id = renamingId.value;
    if (id === null || isRenaming.value) return;

    isRenaming.value = true;

    try {
      const renamed = await renameDraft(id, title);
      options.patchRenamed(id, renamed.title, renamed.updatedAt);
      closeRename();
      toast.showSuccessToast({ title: 'Rascunho renomeado com sucesso.', type: 'success' });
    } catch (error: unknown) {
      toast.showErrorToast({ title: RENAME_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao renomear o rascunho:', error);
    } finally {
      isRenaming.value = false;
    }
  };

  const pendingIds = ref<number[]>([]);

  const refreshAfterDeletion = () => {
    options.refresh().catch((error: unknown) => {
      console.error('Erro ao atualizar a listagem após a exclusão:', error);
    });
  };


  const deleteDraftRefreshingOnRefusal = async (id: number) => {
    try {
      await deleteDraft(id);
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 400) refreshAfterDeletion();
      throw error;
    }
  };

  const deleteDraftsRefreshingOnRefusal = async (ids: number[]) => {
    const response = await deleteDrafts(ids);
    if (response.data.successCount === 0 && response.data.failureCount > 0) {
      refreshAfterDeletion();
    }
    return response;
  };

  const { openDeleteConfirmationModal } = useBulkDelete<{ id: number }>({
    entityName: { singular: 'Rascunho', plural: 'Rascunhos' },
    getConfirmTitle: (count) =>
      count <= 1
        ? 'Tem certeza que deseja deletar o Rascunho?'
        : 'Tem certeza que deseja deletar os Rascunhos?',
    successMessage: 'Rascunho deletado com sucesso.',
    singleDeleteApi: deleteDraftRefreshingOnRefusal,
    bulkDeleteApi: deleteDraftsRefreshingOnRefusal,
    onDeleteSuccess: refreshAfterDeletion,
    onStaleData: refreshAfterDeletion,
    removeFromSelection: options.removeFromSelection,
    selectedCount: () => pendingIds.value.length,
    selectedIds: () => pendingIds.value,
  });

  const openDeleteConfirmation = async (ids: number[]) => {
    if (ids.length === 0) return;
    pendingIds.value = ids;
    await openDeleteConfirmationModal();
  };

  const openSingleDeleteConfirmation = (draft: DraftItem) => openDeleteConfirmation([draft.id]);

  const openBulkDeleteConfirmation = () => openDeleteConfirmation(options.selectedIds());

  return { openRename, openSingleDeleteConfirmation, openBulkDeleteConfirmation };
}
